import {
  createConnectQueryKey,
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from "@connectrpc/connect-query";

import {
  GetSourceArtifactRequest,
  GetSourceArtifactResponse,
  ListSourceArtifactsRequest,
  ListSourceArtifactsRequestSchema,
  ListSourceArtifactsResponse,
} from "@gx/ts-contracts/services/source/api/source_artifact_pb";
import {
  getSourceArtifact,
  listSourceArtifacts,
} from "@gx/ts-contracts/services/source/api/source_artifact-SourceArtifactService_connectquery";
import { PaginationRequestSchema } from "@gx/ts-contracts/shared/pagination_pb";
import { SortingRequestSchema } from "@gx/ts-contracts/shared/sorting_pb";

import {
  DEFAULT_PAGINATION_CURSOR,
  DEFAULT_PAGINATION_TOTAL,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
} from "@/api/constants";
import { createInfiniteQueryInput, UseInfiniteQueryOptions } from "@/api/types";

// ========== GET SOURCE ARTIFACT ==========

export const createGetSourceArtifactQueryKey = (input?: GetSourceArtifactRequest) => {
  return createConnectQueryKey({
    schema: getSourceArtifact,
    input,
    cardinality: "finite",
  });
};

export const useGetSourceArtifactQuery = ({
  input,
  options = {},
}: {
  input?: GetSourceArtifactRequest;
  options?: UseQueryOptions<typeof getSourceArtifact.output, GetSourceArtifactResponse>;
}) => {
  return useQuery<typeof getSourceArtifact.input, typeof getSourceArtifact.output>(
    getSourceArtifact,
    input,
    options,
  );
};

// ========== LIST SOURCE ARTIFACTS ==========

export const createListSourceArtifactsQueryKey = (input?: ListSourceArtifactsRequest) => {
  return createConnectQueryKey({
    schema: listSourceArtifacts,
    input,
    cardinality: "finite",
  });
};

export const useListSourceArtifactsQuery = ({
  input,
  options = {},
}: {
  input?: ListSourceArtifactsRequest;
  options?: UseQueryOptions<typeof listSourceArtifacts.output, ListSourceArtifactsResponse>;
}) => {
  return useQuery<typeof listSourceArtifacts.input, typeof listSourceArtifacts.output>(
    listSourceArtifacts,
    input,
    options,
  );
};

// ========== LIST SOURCE ARTIFACTS INFINITE ==========

export const createListSourceArtifactsInfiniteQueryKey = (input?: ListSourceArtifactsRequest) => {
  return createConnectQueryKey({
    schema: listSourceArtifacts,
    input,
    cardinality: "infinite",
  });
};

export const useListSourceArtifactsInfiniteQuery = ({
  input,
  options = {},
}: {
  input?: ListSourceArtifactsRequest;
  options?: UseInfiniteQueryOptions<
    typeof listSourceArtifacts.input,
    typeof listSourceArtifacts.output,
    "pagination"
  >;
}) => {
  const inputTotal = input?.pagination?.total ?? DEFAULT_PAGINATION_TOTAL;
  const inputCursor = input?.pagination?.cursor ?? DEFAULT_PAGINATION_CURSOR;
  const inputSortBy = input?.sorting?.sortBy ?? DEFAULT_SORT_BY;
  const inputSortOrder = input?.sorting?.sortOrder ?? DEFAULT_SORT_ORDER;

  return useInfiniteQuery<
    typeof listSourceArtifacts.input,
    typeof listSourceArtifacts.output,
    "pagination"
  >(
    listSourceArtifacts,
    createInfiniteQueryInput(ListSourceArtifactsRequestSchema, {
      sourceId: input?.sourceId ?? "",
      pagination: createInfiniteQueryInput(PaginationRequestSchema, {
        total: inputTotal,
        cursor: inputCursor,
      }),
      sorting: createInfiniteQueryInput(SortingRequestSchema, {
        sortBy: inputSortBy,
        sortOrder: inputSortOrder,
      }),
    }),
    {
      pageParamKey: "pagination",
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination?.nextCursor) return undefined;
        return createInfiniteQueryInput(PaginationRequestSchema, {
          total: inputTotal,
          cursor: lastPage.pagination.nextCursor,
        });
      },
      ...options,
    },
  );
};
